import { createAsyncThunk } from '@reduxjs/toolkit';

import { ehErroHttp } from '@/shared/utils/erroHttp';

import type { RootState } from '@/store';

import type {
  ConsultaCnpjResultado,
  CriarContaResultado,
  ErroCriacaoConta,
  TermosVigentes,
} from '../types';
import { classificarErroCriacaoConta } from '../utils/erroCriacaoConta';
import { onboardingSelfService } from './services';

export const carregarTermosOnboardingSelf = createAsyncThunk<TermosVigentes, void>(
  'onboardingSelf/carregarTermos',
  (_, { signal }) => onboardingSelfService.carregarTermos(signal),
);

/**
 * Recebe o CNPJ consultado (e não o lê do estado) para o reducer conseguir
 * descartar a resposta de uma consulta que já não corresponde ao campo.
 */
export const consultarCnpjOnboardingSelf = createAsyncThunk<
  ConsultaCnpjResultado,
  string,
  { rejectValue: 'muitasConsultas' }
>('onboardingSelf/consultarCnpj', async (cnpj, { signal, rejectWithValue }) => {
  try {
    return await onboardingSelfService.consultarCnpj(cnpj, signal);
  } catch (error) {
    if (ehErroHttp(error, 429)) return rejectWithValue('muitasConsultas');
    throw error;
  }
});

export const criarContaOnboardingSelf = createAsyncThunk<
  CriarContaResultado,
  { incluirRespostasDaOperacao: boolean },
  { rejectValue: ErroCriacaoConta; state: RootState }
>(
  'onboardingSelf/criarConta',
  async ({ incluirRespostasDaOperacao }, { getState, signal, rejectWithValue }) => {
    const { empresa, produtos, volume, canal, termosVersao, termosVersaoAceita } =
      getState().onboardingSelf;
    try {
      // A prova de aceite leva a versão que a pessoa aceitou: a vigente pode ter mudado
      // entre o aceite e o envio, e o core aceita mais de uma versão. Sem ela (aceite antes
      // de os termos carregarem e carga falha), vale a vigente, buscada antes de enviar.
      const versao =
        termosVersaoAceita ??
        termosVersao ??
        (await onboardingSelfService.carregarTermos(signal)).versao;
      return await onboardingSelfService.criarConta(
        {
          empresa,
          produtos,
          volume: incluirRespostasDaOperacao ? volume : null,
          canal: incluirRespostasDaOperacao ? canal : null,
          termosVersao: versao,
        },
        signal,
      );
    } catch (error) {
      return rejectWithValue(classificarErroCriacaoConta(error));
    }
  },
  {
    // Síncrona de propósito: o `pending` sai no mesmo tick da primeira chamada, então
    // o segundo clique já encontra `criandoConta` e não gera outra requisição.
    condition: (_, { getState }) => !getState().onboardingSelf.criandoConta,
  },
);
